/**
 * Shared filtering configuration for Elasticsearch queries
 * Handles conditional region and brand filtering based on ACF settings
 */

interface ElasticFilter {
  term?: Record<string, string>;
  terms?: Record<string, string[]>;
}

/**
 * Creates conditional filters based on region and brand ACF settings
 * Returns an array of filters to be applied to Elasticsearch must_not or must clauses
 */
export const createConditionalFilters = (): ElasticFilter[] => {
  const filters: ElasticFilter[] = [];

  const region = window.aqJobsOptions?.region;
  const brand = window.aqJobsOptions?.brand;

  // Add region filter - include both specific region and "all"
  if (region && region !== 'all') {
    filters.push({
      terms: { 'region.keyword': [region, 'all'] },
    });
  }

  // Add brand filter - include both specific brand and "all"
  if (brand && brand !== 'all') {
    filters.push({
      terms: { 'brand.keyword': [brand, 'all'] },
    });
  }

  return filters;
};

/**
 * Creates European markets filtering logic based on locale settings
 * UK: Only UK jobs, exclude all remote jobs
 * EU (France/Germany/Netherlands): Current locale + all remote jobs except UK
 */
export const createEuropeanMarketsFilters = (): { must?: ElasticFilter[]; must_not?: ElasticFilter[] } => {
  const locale = window.aqJobsOptions?.locale;
  const euLocales = ['en_GB', 'fr_FR', 'en_DE', 'en_NL'];

  if (!locale || !euLocales.includes(locale)) {
    return {}; // No EU filtering needed
  }

  // UK-specific logic: Only UK jobs, no remote
  if (locale === 'en_GB') {
    return {
      must: [
        {
          terms: { 'market.keyword': ['United Kingdom'] },
        },
      ],
    };
  }

  // EU non-UK logic: Current market + all remote except UK
  const localeToMarket: Record<string, string> = {
    fr_FR: 'Paris',
    en_DE: 'Germany',
    en_NL: 'Amsterdam',
  };

  const currentMarket = localeToMarket[locale];
  if (!currentMarket) {
    return {};
  }

  return {
    must: [
      {
        bool: {
          should: [
            // Jobs in current market (any offsite preference)
            {
              terms: { 'market.keyword': [currentMarket] },
            },
            // Remote/hybrid jobs from EU markets (exclude UK)
            {
              bool: {
                must: [
                  {
                    terms: { 'offsite_preference.keyword': ['Remote', 'Hybrid'] },
                  },
                  {
                    terms: { 'market.keyword': ['Paris', 'Germany', 'Amsterdam'] },
                  },
                ],
              },
            },
          ],
          minimum_should_match: 1,
        },
      },
    ],
  };
};

/**
 * Applies conditional filters to an existing Elasticsearch query
 * Modifies the query in place by adding filters to the bool.must clause
 * Handles both pre-constructed queries and search-ui generated queries
 */
export const applyConditionalFilters = (query: any): void => {
  const conditionalFilters = createConditionalFilters();
  const euFilters = createEuropeanMarketsFilters();

  if (conditionalFilters.length === 0 && !euFilters.must && !euFilters.must_not) {
    return; // No filters to apply
  }

  // Handle different query structures
  if (!query) {
    return;
  }

  // Ensure query has proper bool structure
  if (!query.bool) {
    query.bool = {};
  }

  if (!query.bool.must) {
    query.bool.must = [];
  }

  if (!query.bool.must_not) {
    query.bool.must_not = [];
  }

  // Add conditional filters to must clause
  query.bool.must.push(...conditionalFilters);

  // Add EU market filters
  if (euFilters.must && euFilters.must.length > 0) {
    query.bool.must.push(...euFilters.must);
  }

  if (euFilters.must_not && euFilters.must_not.length > 0) {
    query.bool.must_not.push(...euFilters.must_not);
  }
};
